import React, { useState, useEffect, useRef } from 'react';

interface VideoModalProps {
  demo: {
    id: string;
    title: string;
    description: string;
    projectName: string;
    category: string;
    videoFileId: string | null;
    videoFileName: string | null;
    artifacts?: Array<{
      id: string;
      name: string;
      mimeType: string;
      size: number;
      webUrl: string;
      downloadUrl?: string;
    }>;
    webUrl: string;
  };
  onClose: () => void;
}

/**
 * Modal component that displays a video player for a demo.
 * Fetches the video streaming URL from the backend and plays inline.
 */
export const VideoModal: React.FC<VideoModalProps> = ({ demo, onClose }) => {
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (demo.videoFileId) {
      fetchVideoUrl(demo.videoFileId);
    }
  }, [demo.videoFileId]);

  const fetchVideoUrl = async (fileId: string) => {
    setLoading(true);
    setError(null);
    try {
      const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000/api';
      // Use the stream proxy endpoint — avoids CORS issues with SharePoint URLs
      setVideoUrl(`${baseUrl}/demos/${fileId}/stream`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load video');
    } finally {
      setLoading(false);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Close on Escape key
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <div>
            <h2 className="modal-title">{demo.title}</h2>
            <p className="modal-subtitle">
              <span className="demo-card__project" style={{ display: 'inline-flex' }}>📁 {demo.category}</span>
              <span style={{ marginLeft: '0.75rem', color: 'var(--text-muted)' }}>{demo.projectName}</span>
            </p>
          </div>
          <button className="modal-close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        {/* Video Player */}
        {demo.videoFileId && (
          <div className="modal-video">
            {loading && (
              <div className="video-loading">
                <div className="spinner" />
                <p>Loading video...</p>
              </div>
            )}
            {error && (
              <div className="video-error">
                <p>⚠️ {error}</p>
                <button className="btn-primary" onClick={() => fetchVideoUrl(demo.videoFileId!)}>
                  Retry
                </button>
              </div>
            )}
            {videoUrl && !loading && !error && (
              <>
                <video
                  ref={videoRef}
                  controls
                  autoPlay
                  playsInline
                  className="video-player"
                  src={videoUrl}
                  onError={() => setError('Video failed to load. The streaming URL may have expired.')}
                >
                  Your browser does not support the video tag.
                </video>
                <div className="video-actions">
                  <a
                    href={videoUrl}
                    download={demo.videoFileName || 'video.mp4'}
                    className="btn-video-download"
                  >
                    ⬇️ Download Video
                  </a>
                </div>
              </>
            )}
            {demo.videoFileName && (
              <p className="video-filename">🎬 {demo.videoFileName}</p>
            )}
          </div>
        )}

        {/* No video */}
        {!demo.videoFileId && (
          <div className="modal-no-video">
            <div className="state-icon">📦</div>
            <p>No video available for this demo.</p>
          </div>
        )}

        {/* Description */}
        <div className="modal-description">
          <p>{demo.description}</p>
        </div>

        {/* Artifacts */}
        {demo.artifacts && demo.artifacts.length > 0 && (
          <div className="modal-artifacts">
            <h3 className="modal-section-title">Artifacts ({demo.artifacts.length})</h3>
            <div className="artifact-list">
              {demo.artifacts.map((artifact) => (
                <ArtifactItem key={artifact.id} artifact={artifact} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/** Individual artifact with download via backend */
const ArtifactItem: React.FC<{
  artifact: {
    id: string;
    name: string;
    mimeType: string;
    size: number;
    webUrl: string;
    downloadUrl?: string;
  };
}> = ({ artifact }) => {
  const [downloadUrl, setDownloadUrl] = useState<string | null>(artifact.downloadUrl || null);

  const handleClick = async (e: React.MouseEvent) => {
    if (downloadUrl) {
      // Already have a direct URL, let the link work naturally
      return;
    }

    e.preventDefault();
    // Fetch download URL from backend
    try {
      const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000/api';
      const response = await fetch(`${baseUrl}/demos/${artifact.id}/video-url`);
      if (response.ok) {
        const data = await response.json();
        setDownloadUrl(data.videoUrl);
        window.open(data.videoUrl, '_blank');
      }
    } catch {
      // Fallback to webUrl
      window.open(artifact.webUrl, '_blank');
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <a
      href={downloadUrl || artifact.webUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="artifact-item"
      onClick={handleClick}
    >
      <span className="artifact-icon">
        {getFileIcon(artifact.mimeType)}
      </span>
      <span className="artifact-name">{artifact.name}</span>
      <span className="artifact-size">{formatFileSize(artifact.size)}</span>
    </a>
  );
};

function getFileIcon(mimeType: string): string {
  if (mimeType.includes('presentation') || mimeType.includes('powerpoint')) return '📊';
  if (mimeType.includes('document') || mimeType.includes('word')) return '📄';
  if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) return '📈';
  if (mimeType.includes('pdf')) return '📕';
  if (mimeType.startsWith('image/')) return '🖼️';
  if (mimeType.startsWith('video/')) return '🎬';
  return '📎';
}
